// Admin → /admin/payment-gateways — multi-PG management dashboard.
//
// Reuses Phase-1 primitives (StatCard, StatusBadge) + same architecture as Travel
// Partners (encrypted creds, masked previews, audit trail, test-connection).
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import {
  Plus, Edit3, Trash2, Search, X, Save, ShieldCheck, Activity, Power,
  AlertTriangle, CheckCircle2, Zap, Wallet, Settings2, Clock, ArrowUp, ArrowDown, RefreshCw,
} from 'lucide-react';
import { api, formatApiErrorDetail } from '../../lib/api';
import { StatCard, EmptyState } from './partnersPrimitives';

const PROVIDER_META = {
  razorpay: { label: 'Razorpay', color: 'bg-blue-100 text-blue-800' },
  cashfree: { label: 'Cashfree', color: 'bg-emerald-100 text-emerald-800' },
  payu:     { label: 'PayU',     color: 'bg-violet-100 text-violet-800' },
  stripe:   { label: 'Stripe',   color: 'bg-indigo-100 text-indigo-800' },
  phonepe:  { label: 'PhonePe',  color: 'bg-purple-100 text-purple-800' },
  paytm:    { label: 'Paytm',    color: 'bg-sky-100 text-sky-800' },
  ccavenue: { label: 'CCAvenue', color: 'bg-amber-100 text-amber-800' },
  other:    { label: 'Other',    color: 'bg-gray-100 text-gray-800' },
};

const HEALTH_BADGE = {
  healthy: { label: 'Healthy', cls: 'bg-green-100 text-green-800 border-green-300', Icon: CheckCircle2 },
  slow:    { label: 'Slow',    cls: 'bg-amber-100 text-amber-800 border-amber-300', Icon: Clock },
  failed:  { label: 'Failed',  cls: 'bg-red-100 text-red-800 border-red-300', Icon: AlertTriangle },
  unknown: { label: 'Untested', cls: 'bg-gray-100 text-gray-700 border-gray-300', Icon: Activity },
};

const STATUS_BADGE = {
  Active:    'bg-green-100 text-green-800 border-green-300',
  Pending:   'bg-amber-100 text-amber-800 border-amber-300',
  Suspended: 'bg-red-100 text-red-800 border-red-300',
  Draft:     'bg-gray-100 text-gray-700 border-gray-300',
};

const baseInput = 'rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-3 py-2 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 focus:outline-none';

const Field = ({ label, children, span = 1 }) => (
  <label className={`flex flex-col gap-1 min-w-0 sm:col-span-${span}`}>
    <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">{label}</span>
    {children}
  </label>
);

// ── Editor ───────────────────────────────────────────────────────────
const PGEditor = ({ open, onClose, value, meta, onSaved }) => {
  const [form, setForm] = useState(value || {});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(value || { provider: 'razorpay', status: 'Pending', priority: 50, credentials: {}, settings: { environment: 'sandbox', currency: 'INR', payment_methods: ['card', 'upi', 'netbanking'] } });
  }, [value, open]);

  if (!open) return null;
  const isEdit = !!form.id;
  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));
  const setCred = (k) => (e) => setForm((s) => ({ ...s, credentials: { ...(s.credentials || {}), [k]: e.target.value } }));
  const setSetting = (k) => (e) => setForm((s) => ({ ...s, settings: { ...(s.settings || {}), [k]: e?.target ? e.target.value : e } }));
  const togglePayMethod = (method) => setForm((s) => {
    const cur = s.settings?.payment_methods || [];
    const next = cur.includes(method) ? cur.filter((m) => m !== method) : [...cur, method];
    return { ...s, settings: { ...(s.settings || {}), payment_methods: next } };
  });

  const save = async () => {
    if (!form.name || !form.provider) { toast.error('Name and provider are required'); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      delete payload.health; delete payload.last_tested_at; delete payload.created_at; delete payload.updated_at;
      const r = isEdit
        ? await api.put(`/admin/payment-gateways/${form.id}`, payload)
        : await api.post('/admin/payment-gateways', payload);
      toast.success(isEdit ? 'Gateway updated' : 'Gateway created');
      onSaved(r.data);
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70]" data-testid="pg-editor">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <aside className="absolute top-0 right-0 h-full w-full sm:w-[640px] max-w-full bg-white dark:bg-gray-900 shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-5 h-14 border-b border-gray-200 dark:border-gray-800">
          <h2 className="font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Wallet className="h-5 w-5 text-red-600" />
            {isEdit ? 'Edit gateway' : 'New gateway'}
          </h2>
          <button onClick={onClose} className="tap-target p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800" data-testid="pg-editor-close" aria-label="Close">
            <X className="h-5 w-5 text-gray-600 dark:text-gray-400" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Name"><input value={form.name || ''} onChange={set('name')} className={baseInput} data-testid="pg-field-name" placeholder="Razorpay Primary" /></Field>
            <Field label="Display name"><input value={form.display_name || ''} onChange={set('display_name')} className={baseInput} placeholder="Razorpay" /></Field>
            <Field label="Provider">
              <select value={form.provider || 'razorpay'} onChange={set('provider')} className={baseInput} data-testid="pg-field-provider">
                {(meta?.providers || Object.keys(PROVIDER_META)).map((p) => <option key={p} value={p}>{PROVIDER_META[p]?.label || p}</option>)}
              </select>
            </Field>
            <Field label="Status">
              <select value={form.status || 'Pending'} onChange={set('status')} className={baseInput} data-testid="pg-field-status">
                {(meta?.statuses || ['Active', 'Pending', 'Suspended', 'Draft']).map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="Priority (lower = preferred)"><input type="number" min={1} max={1000} value={form.priority ?? 50} onChange={(e) => set('priority')({ target: { value: Number(e.target.value) } })} className={baseInput} /></Field>
            <Field label="Logo URL"><input value={form.logo_url || ''} onChange={set('logo_url')} className={baseInput} /></Field>
          </div>

          {/* Credentials */}
          <div>
            <p className="text-xs uppercase font-semibold tracking-wider text-gray-500 mb-2 flex items-center gap-1">
              <ShieldCheck className="h-3.5 w-3.5" /> Credentials (encrypted at rest)
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Merchant ID"><input value={form.credentials?.merchant_id || ''} onChange={setCred('merchant_id')} className={baseInput} /></Field>
              <Field label="Key ID"><input value={form.credentials?.key_id || ''} onChange={setCred('key_id')} className={baseInput} data-testid="pg-field-key-id" /></Field>
              <Field label="Secret Key"><input type="password" value={form.credentials?.secret_key || ''} onChange={setCred('secret_key')} className={baseInput} data-testid="pg-field-secret-key" placeholder={isEdit ? 'Leave blank to keep existing' : ''} /></Field>
              <Field label="API Key"><input type="password" value={form.credentials?.api_key || ''} onChange={setCred('api_key')} className={baseInput} /></Field>
              <Field label="Webhook Secret"><input type="password" value={form.credentials?.webhook_secret || ''} onChange={setCred('webhook_secret')} className={baseInput} /></Field>
              <Field label="Salt"><input value={form.credentials?.salt || ''} onChange={setCred('salt')} className={baseInput} /></Field>
            </div>
            {isEdit && <p className="text-[11px] text-gray-400 mt-1">Tip: existing masked values are kept if you don't change them.</p>}
          </div>

          {/* Settings */}
          <div>
            <p className="text-xs uppercase font-semibold tracking-wider text-gray-500 mb-2 flex items-center gap-1">
              <Settings2 className="h-3.5 w-3.5" /> Settings
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Environment">
                <select value={form.settings?.environment || 'sandbox'} onChange={setSetting('environment')} className={baseInput} data-testid="pg-field-env">
                  {(meta?.environments || ['sandbox', 'live']).map((e) => <option key={e} value={e}>{e}</option>)}
                </select>
              </Field>
              <Field label="Currency">
                <input value={form.settings?.currency || 'INR'} onChange={setSetting('currency')} className={baseInput} />
              </Field>
              <Field label="Txn fee %"><input type="number" step="0.1" value={form.settings?.transaction_fee_percent ?? 2.0} onChange={(e) => setSetting('transaction_fee_percent')(Number(e.target.value))} className={baseInput} /></Field>
              <Field label="Settlement T+ days"><input type="number" value={form.settings?.settlement_t_plus_days ?? 2} onChange={(e) => setSetting('settlement_t_plus_days')(Number(e.target.value))} className={baseInput} /></Field>
              <Field label="Timeout (s)"><input type="number" value={form.settings?.timeout_seconds ?? 20} onChange={(e) => setSetting('timeout_seconds')(Number(e.target.value))} className={baseInput} /></Field>
              <Field label="Refund window (days)"><input type="number" value={form.settings?.refund_window_days ?? 7} onChange={(e) => setSetting('refund_window_days')(Number(e.target.value))} className={baseInput} /></Field>
              <Field label="Callback URL" span={2}><input value={form.settings?.callback_url || ''} onChange={setSetting('callback_url')} className={baseInput} /></Field>
              <Field label="Webhook URL" span={2}><input value={form.settings?.webhook_url || ''} onChange={setSetting('webhook_url')} className={baseInput} /></Field>
              <Field label="Notes" span={2}><textarea value={form.settings?.notes || ''} onChange={setSetting('notes')} rows={2} className={baseInput} /></Field>
            </div>

            <div className="mt-3">
              <p className="text-[11px] uppercase tracking-wider font-semibold text-gray-500 mb-1.5">Payment methods</p>
              <div className="flex flex-wrap gap-2">
                {(meta?.payment_methods || ['card', 'upi', 'netbanking', 'wallet']).map((m) => {
                  const on = (form.settings?.payment_methods || []).includes(m);
                  return (
                    <button key={m} type="button" onClick={() => togglePayMethod(m)} data-testid={`pg-paymethod-${m}`}
                            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${on ? 'bg-red-50 text-red-700 border-red-300' : 'bg-white dark:bg-gray-950 text-gray-600 border-gray-300'}`}>
                      {m}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-800 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="pg-editor-save" className="inline-flex items-center gap-2 rounded-lg bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white px-5 py-2.5 text-sm font-semibold shadow">
            <Save className="h-4 w-4" /> {saving ? 'Saving…' : 'Save gateway'}
          </button>
        </div>
      </aside>
    </div>
  );
};

// ── Main page ────────────────────────────────────────────────────────
const AdminPaymentGateways = () => {
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [testing, setTesting] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, s] = await Promise.all([
        api.get('/admin/payment-gateways'),
        api.get('/admin/payment-gateways/stats'),
      ]);
      setItems(list.data.items || []);
      setStats(s.data);
    } catch (e) {
      toast.error('Failed to load gateways');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      try { const r = await api.get('/admin/payment-gateways/meta'); setMeta(r.data); } catch { /* ignore */ }
    })();
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleActive = async (g) => {
    try {
      await api.post(`/admin/payment-gateways/${g.id}/${g.status === 'Active' ? 'deactivate' : 'activate'}`);
      toast.success(g.status === 'Active' ? 'Deactivated' : 'Activated');
      load();
    } catch { toast.error('Status change failed'); }
  };

  const testConn = async (id) => {
    setTesting((s) => ({ ...s, [id]: true }));
    try {
      const r = await api.post(`/admin/payment-gateways/${id}/test`);
      toast[r.data?.ok ? 'success' : 'error'](`${r.data?.state || 'tested'} · ${r.data?.latency_ms || '—'} ms`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || 'Test failed');
    } finally {
      setTesting((s) => ({ ...s, [id]: false }));
    }
  };

  const remove = async (id) => {
    if (!confirm('Delete this gateway? Existing transactions are kept.')) return;
    try { await api.delete(`/admin/payment-gateways/${id}`); toast.success('Deleted'); load(); }
    catch { toast.error('Delete failed'); }
  };

  const move = async (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= items.length) return;
    const arr = [...items];
    const [m] = arr.splice(idx, 1);
    arr.splice(target, 0, m);
    setItems(arr);
    try { await api.post('/admin/payment-gateways/reorder', { order: arr.map((i) => i.id) }); }
    catch { load(); }
  };

  const healthDistribution = stats?.health || {};

  return (
    <div className="space-y-6" data-testid="admin-pg-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Wallet className="h-7 w-7 text-red-600" />
            Payment Gateways
          </h1>
          <p className="text-sm text-gray-500 mt-1">Manage credentials, health and priorities for all checkout providers.</p>
        </div>
        <button onClick={() => { setEditing(null); setEditorOpen(true); }} data-testid="pg-new-btn"
                className="inline-flex items-center gap-2 rounded-lg bg-red-600 hover:bg-red-700 text-white px-5 py-2.5 text-sm font-semibold shadow tap-target">
          <Plus className="h-4 w-4" /> Add gateway
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <StatCard label="Total" value={stats?.total ?? '—'} accent="gray" testId="pg-stat-total" />
        <StatCard label="Active" value={stats?.active ?? '—'} accent="green" testId="pg-stat-active" sub="Available in checkout" />
        <StatCard label="Pending" value={stats?.pending ?? '—'} accent="amber" testId="pg-stat-pending" sub="Awaiting credentials" />
        <StatCard label="Healthy" value={healthDistribution.healthy ?? 0} accent="green" testId="pg-stat-healthy" />
        <StatCard label="Failed" value={healthDistribution.failed ?? 0} accent="red" testId="pg-stat-failed" sub="Last test failed" />
      </div>

      {loading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : items.length === 0 ? (
        <EmptyState
          title="No payment gateways yet"
          description="Add your first provider — credentials are encrypted at rest and never shown unmasked in the UI."
          action={<button onClick={() => { setEditing(null); setEditorOpen(true); }} className="inline-flex items-center gap-2 rounded-lg bg-red-600 hover:bg-red-700 text-white px-5 py-2.5 text-sm font-semibold shadow"><Plus className="h-4 w-4" /> Add gateway</button>}
        />
      ) : (
        <ul className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {items.map((g, idx) => {
            const provMeta = PROVIDER_META[g.provider] || PROVIDER_META.other;
            const health = HEALTH_BADGE[g.health?.state || 'unknown'];
            return (
              <li key={g.id} data-testid={`pg-card-${g.id}`} className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-bold text-gray-900 dark:text-white text-base truncate">{g.name}</h3>
                      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${provMeta.color}`}>{provMeta.label}</span>
                      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${STATUS_BADGE[g.status] || STATUS_BADGE.Draft}`}>{g.status}</span>
                      <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border ${health.cls}`}>
                        <health.Icon className="h-3 w-3" /> {health.label}
                        {g.health?.latency_ms != null && <span className="font-mono">· {g.health.latency_ms} ms</span>}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1 font-mono">{g.id} · priority {g.priority}</p>
                    <p className="text-xs text-gray-500 mt-1">env: <strong>{g.settings?.environment || 'sandbox'}</strong> · {g.settings?.currency || 'INR'} · {(g.settings?.payment_methods || []).join(' · ') || '—'}</p>
                  </div>
                </div>

                <div className="mt-3 flex items-center gap-1 flex-wrap">
                  <button data-testid={`pg-test-${g.id}`} onClick={() => testConn(g.id)} disabled={testing[g.id]} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white tap-target">
                    {testing[g.id] ? <RefreshCw className="h-3 w-3 animate-spin" /> : <Zap className="h-3 w-3" />} Test
                  </button>
                  <button onClick={() => toggleActive(g)} className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg tap-target ${g.status === 'Active' ? 'bg-amber-100 text-amber-800' : 'bg-green-600 text-white hover:bg-green-700'}`}>
                    <Power className="h-3 w-3" /> {g.status === 'Active' ? 'Deactivate' : 'Activate'}
                  </button>
                  <button data-testid={`pg-edit-${g.id}`} onClick={() => { setEditing(g); setEditorOpen(true); }} className="tap-target p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-800"><Edit3 className="h-4 w-4 text-gray-600" /></button>
                  <button onClick={() => move(idx, -1)} disabled={idx === 0} className="tap-target p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30"><ArrowUp className="h-4 w-4 text-gray-600" /></button>
                  <button onClick={() => move(idx, 1)} disabled={idx === items.length - 1} className="tap-target p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30"><ArrowDown className="h-4 w-4 text-gray-600" /></button>
                  <button data-testid={`pg-delete-${g.id}`} onClick={() => remove(g.id)} className="tap-target p-2 rounded hover:bg-red-50 ml-auto"><Trash2 className="h-4 w-4 text-red-600" /></button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <PGEditor open={editorOpen} value={editing} meta={meta} onClose={() => setEditorOpen(false)} onSaved={() => load()} />
    </div>
  );
};

export default AdminPaymentGateways;
